import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import {
  IndianRupee, ArrowLeft, ChevronRight, ChevronDown, FileText, Mail, Upload, Download,
  AlertTriangle, FolderOpen, Layers, MessageSquarePlus, Clock, Send,
  TrendingUp, CheckCircle2, Banknote,
} from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function PoFollowupPortal() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/sales/po-followup")
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setData({ totals: {}, projects: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (data?.projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="min-h-full bg-zinc-50" data-testid="po-followup-page">
      <div className="p-5 sm:p-8 max-w-6xl mx-auto">
        {data === null ? (
          <div className="py-24 text-center text-zinc-400">Loading…</div>
        ) : active ? (
          <ProjectView project={active} onBack={() => setCode(null)} onChanged={load} />
        ) : (
          <Overview data={data} onOpen={setCode} />
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const tones = {
    indigo: "from-indigo-600 to-indigo-500",
    amber: "from-amber-500 to-orange-500",
    emerald: "from-emerald-600 to-emerald-500",
    zinc: "from-zinc-800 to-zinc-700",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tones[tone]} text-white p-5 shadow-sm`} data-testid={`po-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/80 text-xs font-medium uppercase tracking-wider">
        <Icon size={15} strokeWidth={1.8} /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function Overview({ data, onOpen }) {
  const t = data.totals || {};
  const projects = data.projects || [];
  return (
    <div data-testid="po-followup-overview">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow">
          <Banknote size={22} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">PO Follow-up</h1>
          <p className="text-sm text-zinc-500">Chase the purchase orders — see commercials and exactly how much value is still awaiting a PO.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Awaiting PO" value={inr(t.awaiting_po_value)} icon={AlertTriangle} tone="amber" />
        <Kpi label="Lines to chase" value={t.awaiting_count || 0} icon={Clock} tone="indigo" />
        <Kpi label="Email confirmed" value={t.email_confirmed_count || 0} icon={Mail} tone="zinc" />
        <Kpi label="Revised value" value={inr(t.revised_value)} icon={TrendingUp} tone="emerald" />
      </div>

      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">
        {t.projects_awaiting || 0} of {t.projects || 0} project(s) awaiting a PO
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <button key={p.project_code} onClick={() => onOpen(p.project_code)}
            data-testid={`po-followup-project-${p.project_code}`}
            className="w-full text-left rounded-2xl border border-zinc-200 bg-white p-5 hover:border-indigo-300 hover:shadow-md transition-all flex items-center gap-4">
            <div className={`w-1.5 self-stretch rounded-full ${p.awaiting_count > 0 ? "bg-amber-400" : "bg-emerald-400"}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                {p.client_name && <span className="text-[11px] text-zinc-400">· {p.client_name}</span>}
              </div>
              <div className="font-semibold text-zinc-900 truncate">{p.name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="text-zinc-500">Revised <b className="text-zinc-700">{inr(p.revised_value)}</b></span>
                {p.awaiting_count > 0 ? (
                  <span className="text-amber-700 font-medium">{inr(p.awaiting_po_value)} awaiting · {p.awaiting_count} line(s)</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 size={13} /> all PO'd</span>
                )}
                {p.email_confirmed_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-zinc-500"><Mail size={12} /> {p.email_confirmed_count} email confirmed</span>
                )}
                {p.notes?.length > 0 && <span className="text-indigo-500">{p.notes.length} note(s)</span>}
                {p.documents?.length > 0 && <span className="inline-flex items-center gap-1 text-zinc-500"><Download size={12} /> {p.documents.length} file(s)</span>}
              </div>
            </div>
            <ChevronRight size={18} className="text-zinc-300 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ProjectView({ project, onBack, onChanged }) {
  const p = project;
  return (
    <div data-testid="po-followup-view">
      <button onClick={onBack} data-testid="po-followup-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="mb-6">
        <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase">{p.project_code}{p.client_name ? ` · ${p.client_name}` : ""}</span>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{p.name}</h1>
      </div>

      {/* Commercials */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <CommCard label="Original value" value={inr(p.original_value)} />
        <CommCard label="Approved variations" value={inr(p.approved_variation_value)} accent="emerald" />
        <CommCard label="Pending variations" value={inr(p.pending_variation_value)} accent="violet" />
        <CommCard label="Revised value" value={inr(p.revised_value)} accent="indigo" big />
      </div>
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-6 flex items-center justify-between" data-testid="po-followup-awaiting-banner">
        <span className="text-sm text-amber-800 font-medium flex items-center gap-2">
          <AlertTriangle size={16} /> Value still awaiting a PO
        </span>
        <span className="text-lg font-bold text-amber-900 tabular-nums">{inr(p.awaiting_po_value)} · {p.awaiting_count} line(s)</span>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Awaiting lines */}
        <div className="lg:col-span-3 space-y-3">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Lines awaiting a PO</div>
          {p.awaiting.length === 0 ? (
            <div className="py-12 text-center text-emerald-600 border border-dashed border-emerald-200 rounded-2xl bg-emerald-50/50">
              <CheckCircle2 size={22} className="mx-auto mb-2" /> Every line is backed by a PO. Nothing to chase.
            </div>
          ) : (
            <>
              <BaseClubCard lines={p.awaiting.filter((a) => a.kind === "base")} code={p.project_code} onChanged={onChanged} />
              {p.awaiting.filter((a) => a.kind !== "base").map((a) => (
                <AwaitingCard key={a.boq_id} a={a} code={p.project_code} onChanged={onChanged} />
              ))}
            </>
          )}
        </div>
        {/* Follow-up chase log */}
        <div className="lg:col-span-2 space-y-6">
          <ChaseLog code={p.project_code} notes={p.notes || []} onChanged={onChanged} />
          <DocumentsCard documents={p.documents || []} />
        </div>
      </div>
    </div>
  );
}

function DocumentsCard({ documents }) {
  const pos = documents.filter((d) => d.category === "PO");
  const emails = documents.filter((d) => d.category === "Email");
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return ""; } };
  const Row = ({ d }) => (
    <a href={d.url} target="_blank" rel="noreferrer" download
      data-testid={`po-followup-doc-${d.category}-${(d.url || "").split("/").pop()}`}
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-zinc-50 group">
      <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${d.category === "PO" ? "bg-zinc-900 text-white" : "bg-amber-100 text-amber-700"}`}>
        {d.category === "PO" ? <FileText size={14} /> : <Mail size={14} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-zinc-800 truncate">{d.name}</span>
        <span className="block text-[11px] text-zinc-400 truncate">{d.scope}{d.lines ? ` · ${d.lines}` : ""}{d.ref ? ` · ${d.ref}` : ""}{d.at ? ` · ${fmt(d.at)}` : ""}</span>
      </span>
      <Download size={15} className="text-zinc-300 group-hover:text-indigo-600 shrink-0" />
    </a>
  );
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="po-followup-documents">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
          <FileText size={14} /> PO &amp; Email documents
        </div>
        <span className="text-[11px] text-zinc-400">{documents.length} file(s)</span>
      </div>
      {documents.length === 0 ? (
        <p className="text-xs text-zinc-400 py-4 text-center">No PO or email files uploaded yet.</p>
      ) : (
        <div className="space-y-3">
          {pos.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 px-3 mb-1">Purchase Orders ({pos.length})</div>
              <div className="space-y-0.5">{pos.map((d, i) => <Row key={`po${i}`} d={d} />)}</div>
            </div>
          )}
          {emails.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 px-3 mb-1">Email confirmations ({emails.length})</div>
              <div className="space-y-0.5">{emails.map((d, i) => <Row key={`em${i}`} d={d} />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommCard({ label, value, accent = "zinc", big }) {
  const txt = { zinc: "text-zinc-900", emerald: "text-emerald-700", violet: "text-violet-700", indigo: "text-indigo-700" }[accent];
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">{label}</div>
      <div className={`mt-1.5 font-bold tracking-tight tabular-nums ${big ? "text-2xl" : "text-xl"} ${txt}`}>{value}</div>
    </div>
  );
}

const KIND_META = {
  base: { label: "Base PO needed", icon: Layers, cls: "bg-rose-50 text-rose-600", chip: "bg-rose-100 text-rose-800" },
  variation: { label: "Variation", icon: FolderOpen, cls: "bg-violet-50 text-violet-600", chip: "bg-violet-100 text-violet-800" },
  breach: { label: "Scope breach", icon: AlertTriangle, cls: "bg-rose-50 text-rose-600", chip: "bg-rose-100 text-rose-800" },
};

function BaseClubCard({ lines, code, onChanged }) {
  const [sel, setSel] = useState(() => new Set(lines.map((l) => l.boq_id)));
  const [url, setUrl] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [saving, setSaving] = useState(false);
  if (!lines.length) return null;
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selCount = sel.size;
  const selValue = lines.filter((l) => sel.has(l.boq_id)).reduce((t, l) => t + (l.amount || 0), 0);

  async function upload(docType) {
    if (!url) { toast.error(docType === "email" ? "Attach the email confirmation" : "Attach the PO file"); return; }
    const ids = lines.map((l) => l.boq_id).filter((id) => sel.has(id));
    if (!ids.length) { toast.error("Select the line(s) this covers"); return; }
    const name = url.split("/").pop() || (docType === "email" ? "email" : "po");
    setSaving(true);
    try {
      await api.post(`/projects/${code}/line-pos`, { file_url: url, file_name: name, po_number: poNumber, boq_ids: ids, doc_type: docType });
      toast.success(docType === "email"
        ? `Email confirmation attached to ${ids.length} line(s) — awaiting the PO.`
        : `PO attached — ${ids.length} line(s) covered.`);
      setUrl(""); setPoNumber(""); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden border-l-4 border-l-rose-400" data-testid="po-followup-base-club">
      <div className="px-4 py-3 flex items-center gap-3 border-b border-zinc-100">
        <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><Layers size={17} /></div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-zinc-900">Base PO needed · {lines.length} line(s)</div>
          <div className="text-xs text-zinc-400">Tick the lines one PO covers, then attach it once — no need to repeat per line.</div>
        </div>
        <span className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-lg">{inr(selValue)} · {selCount} selected</span>
      </div>
      <div className="divide-y divide-zinc-100">
        {lines.map((l) => {
          const on = sel.has(l.boq_id);
          return (
            <button key={l.boq_id} onClick={() => toggle(l.boq_id)} data-testid={`po-followup-base-pick-${l.boq_id}`}
              className="w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-zinc-50">
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-zinc-900 border-zinc-900" : "border-zinc-300"}`}>
                {on && <CheckCircle2 size={11} className="text-white" />}
              </span>
              <span className={`flex-1 text-sm truncate ${on ? "text-zinc-800" : "text-zinc-400"}`}>{l.material_name || l.label}</span>
              {l.email_confirmed && (
                <a href={l.email_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                   data-testid={`po-followup-base-email-${l.boq_id}`}
                   className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">
                  <Mail size={10} /> Email
                </a>
              )}
              <span className="text-xs text-zinc-400 font-mono shrink-0">{l.quantity ? `${l.quantity} ${l.unit} · ` : ""}{inr(l.amount)}</span>
            </button>
          );
        })}
      </div>
      <div className="p-4 space-y-3 bg-zinc-50/60">
        <FileUpload value={url} label="Attach one PO or email confirmation for the selected lines" onChange={setUrl} testid="po-followup-base-upload" />
        <div className="flex flex-wrap items-center gap-2">
          <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO number"
            data-testid="po-followup-base-ponum"
            className="text-sm rounded-lg border border-zinc-300 px-3 py-2 max-w-[160px] focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          <button onClick={() => upload("po")} disabled={saving || !url || !selCount} data-testid="po-followup-base-po-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
            <FileText size={15} /> {saving ? "Saving…" : `Upload PO · ${selCount}`}
          </button>
          <button onClick={() => upload("email")} disabled={saving || !url || !selCount} data-testid="po-followup-base-email-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 text-white px-4 py-2 text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
            <Mail size={15} /> Email confirmation · {selCount}
          </button>
        </div>
      </div>
    </div>
  );
}

function AwaitingCard({ a, code, onChanged }) {
  const m = KIND_META[a.kind] || KIND_META.base;
  const Icon = m.icon;
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  async function uploadDoc(docType) {
    if (!url) { toast.error(docType === "email" ? "Attach the email confirmation" : "Attach the PO file"); return; }
    const name = url.split("/").pop() || (docType === "email" ? "email" : "po");
    setSaving(true);
    try {
      if (a.kind === "base") {
        await api.post(`/projects/${code}/line-pos`, { file_url: url, file_name: name, boq_ids: [a.boq_id], doc_type: docType });
      } else if (a.kind === "variation") {
        await api.post(`/projects/${code}/boq/${a.boq_id}/variation-po`, { file_url: url, file_name: name, doc_type: docType });
      } else {
        await api.post(`/projects/${code}/boq/${a.boq_id}/amended-po`, { file_url: url, file_name: name, doc_type: docType });
      }
      toast.success(docType === "email" ? "Email confirmation attached — awaiting the actual PO." : "PO attached.");
      setUrl(""); setOpen(false); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }

  return (
    <div className={`rounded-2xl border bg-white border-l-4 ${a.email_confirmed ? "border-l-amber-400" : "border-l-rose-400"} border-zinc-200`}
      data-testid={`po-followup-line-${a.boq_id}`}>
      <div className="px-4 py-3 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.cls}`}><Icon size={17} /></div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-zinc-900 truncate">{a.material_name || a.label}</div>
          <div className="text-xs text-zinc-400">
            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-mono uppercase mr-1.5 ${m.chip}`}>{m.label}</span>
            {a.quantity ? `${a.quantity} ${a.unit} · ` : ""}{inr(a.amount)}
          </div>
        </div>
        {a.email_confirmed ? (
          <a href={a.email_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            data-testid={`po-followup-email-link-${a.boq_id}`}>
            <Mail size={12} /> Email on file
          </a>
        ) : null}
        <button onClick={() => setOpen((o) => !o)} data-testid={`po-followup-attach-toggle-${a.boq_id}`}
          className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100">
          <Upload size={13} /> Attach PO / email
          <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-zinc-100">
          <FileUpload value={url} label="Attach PO or email confirmation"
            onChange={setUrl} testid={`po-followup-email-upload-${a.boq_id}`} />
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => uploadDoc("po")} disabled={saving || !url} data-testid={`po-followup-po-save-${a.boq_id}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
              <FileText size={15} /> {saving ? "Saving…" : "Save PO"}
            </button>
            <button onClick={() => uploadDoc("email")} disabled={saving || !url} data-testid={`po-followup-email-save-${a.boq_id}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 text-white px-4 py-2 text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
              <Mail size={15} /> Save email confirmation
            </button>
          </div>
          <p className="text-[11px] text-zinc-400">Attach the actual PO to close this line, or the client's email confirmation while you chase the PO.</p>
        </div>
      )}
    </div>
  );
}

function ChaseLog({ code, notes, onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  async function add() {
    if (!text.trim()) { toast.error("Write a follow-up note"); return; }
    setSaving(true);
    try {
      await api.post("/sales/followup-note", { project_code: code, text: text.trim() });
      toast.success("Follow-up logged.");
      setText(""); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="po-followup-chaselog">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">
        <MessageSquarePlus size={14} /> Follow-up log
      </div>
      <div className="flex gap-2 mb-4">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder="e.g. Called client — PO expected by Friday"
          data-testid="po-followup-note-input"
          className="flex-1 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none" />
        <button onClick={add} disabled={saving || !text.trim()} data-testid="po-followup-note-save"
          className="self-end inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          <Send size={15} />
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="text-xs text-zinc-400 py-4 text-center">No follow-ups logged yet.</p>
      ) : (
        <div className="space-y-3 max-h-[380px] overflow-y-auto">
          {notes.map((n) => (
            <div key={n.id} className="text-sm border-l-2 border-indigo-200 pl-3" data-testid={`po-followup-note-${n.id}`}>
              <p className="text-zinc-700 whitespace-pre-wrap break-words">{n.text}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">{n.by} · {fmt(n.at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
